import React, { useState, createContext, useContext, useEffect, useMemo, useCallback } from "react";
import { ActivityEnums, OrderEnums, OrderEnumsLists, Roles, ROLES_CLAIM } from "../common/constants";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { GetUserReference } from "../common/orders";
import { useNavigate } from "react-router-dom";
import { MakeActivity } from "../common/activity";
import { useEntityFunctions, useAuth, useGET, useSignal, useEntities, Task, useEntityById } from "@emberly/zenith-client";
import config from "../config";
import UpdateRequiredPage from "../components/pages/error/UpdateRequiredPage";
import CreateMissionDialog from "../components/common/inputs/CreateMissionDialog";
import { useNotification } from "./NotificationProvider";

export const StationContext = createContext();
export const useStation = () => useContext(StationContext);

export default function StationContextProvider(props) {

  const navigate = useNavigate();
  const { connection, connecting } = useSignal();
  const { getAccessTokenSilently, user } = useAuth();

  const [updating, setUpdating] = useState(false);
  const [activeUsers, setActiveUsers] = useState(new Set());
  const [showActivityLog, setShowActivityLog] = useState(false);
  const { t } = useTranslation();

  const { createEntity: createActivityLogEntity } = useEntityFunctions("ActivityLog", "cli");
  const { entities: warehouses } = useEntities("Warehouse");
  const { createEntity: createMissionEntity } = useEntityFunctions("Mission");
  const { push } = useNotification();

  const orgId = user?.org_id;
  const { entity: exportSettings, loading: loadingExportSettings, updateEntity: updateExportSettings } = useEntityById("ExportConfig", orgId, !!orgId, true);

  const { entity: station, loading } = useEntityById("Station", orgId, !connecting);
  const updateRequired = !!station && station?.minimumApiVersion > config.apiVersion;

  useEffect(() => {
    if (!connecting && !!connection) {

      const onUserConnected = (orgId, userId) => {
        setActiveUsers(users => new Set([...users, userId]));
        connection.invoke("Heartbeat");
      };

      const onUserDisconnected = (orgId, userId) => {
        setActiveUsers(users => new Set([...users].filter(t => t !== userId)));
      };

      const onHeartbeat = (orgId, userId) => {
        setActiveUsers(users => new Set([...users, userId]));
      };

      connection.on("OnUserConnected", onUserConnected);
      connection.on("OnUserDisconnected", onUserDisconnected);
      connection.on("OnHeartbeat", onHeartbeat);


      return () => {
        connection.off("OnUserConnected", onUserConnected);
        connection.off("OnUserDisconnected", onUserDisconnected);
        connection.off("OnHeartbeat", onHeartbeat);
      };
    }
  }, [connection, connecting]);

  const updateStation = useCallback(async (request) => {
    try {
      setUpdating(true);

      await axios.patch(
        "/api/station",
        request,
        {
          headers: { Authorization: `Bearer ${await getAccessTokenSilently()}` },
        }
      );

    } catch (err) {
      console.log(err);
    } finally {
      setUpdating(false);
    }
  }, [getAccessTokenSilently]);

  const logEvent = useCallback(async (data, overrideUser = null) => {
    try {
      await createActivityLogEntity({
        sender: GetUserReference(overrideUser || user),
        avatar: (overrideUser || user).picture,
        link: window?.location?.pathname,
        ...data
      });
    } catch (err) {
      console.log("log error", err);
    }
  }, [createActivityLogEntity, user]);


  const pushToDispatch = useCallback((data) => {
    if (!!station?.dispatcherOnDuty?.id) {

      push(station.dispatcherOnDuty.id, {
        cancelAction: t("common:close"),
        confirmAction: t("notification:openAction"),
        sender: GetUserReference(user),
        ...data
      });
    }
  }, [push, station, user, t]);

  const [createMissionTask, setCreateMissionTask] = useState(null);

  const createMissionDraft = useCallback(async () => {

    try {
      const t = new Task();
      setCreateMissionTask(t);

      const missionData = await t.wait();
      const mission = await createMissionEntity(missionData);

      logEvent(MakeActivity(ActivityEnums.Category.Mission, ActivityEnums.Type.MissionDraftCreated, mission?.id, "", "", null, mission?.number));
      navigate(`/drafts/${mission.id}`, { relative: "path" });

    } catch (err) {
      console.log(err);
    } finally {
      setCreateMissionTask(null);
    }

  }, [createMissionEntity, navigate, logEvent]);


  const { isDriver, isAccountant, isOperator, isStationAdmin, isGlobalAdmin, permissions, roles } = useMemo(() => {
    const roles = new Set(user[ROLES_CLAIM] || []);
    const isDriver = roles.has(Roles.Driver);
    const isAccountant = roles.has(Roles.Accountant);
    const isStationAdmin = roles.has(Roles.StationAdmin);
    const isGlobalAdmin = roles.has(Roles.GlobalAdmin);
    const isOperator = roles.has(Roles.Operator);

    return {
      roles,
      isDriver,
      isOperator,
      isAccountant,
      isStationAdmin,
      isGlobalAdmin,
      permissions: {
        canCompleteMission: isStationAdmin || isAccountant,
        canSetDispatcherOnDuty: isOperator || isStationAdmin,
        canSendMissionRequest: isOperator || isStationAdmin
      },
    };
  }, [user]);


  return (
    <StationContext.Provider
      value={{
        loading: loading,
        activeUsers, // active users will return your own userId if there is any other instances of your user currently online.
        roles,
        user,
        station,
        updating,
        isDriver,
        isAccountant,
        isStationAdmin,
        isGlobalAdmin,
        isOperator,
        warehouses,
        permissions,
        pushToDispatch,
        updateStation,
        priceUnit: OrderEnums.Currency.NOK,
        priceUnitShorthand: "kr",
        orgId,
        createMissionDraft,
        showActivityLog,
        setShowActivityLog,
        logEvent,
        exportSettings,
        loadingExportSettings,
        updateExportSettings
      }}>
      {updateRequired ? <UpdateRequiredPage /> : props.children}
      {!!createMissionTask ? <CreateMissionDialog task={createMissionTask} /> : null}
    </StationContext.Provider>
  );
}

export const useRoles = () => {
  const { data } = useGET("client/roles/list");

  return useMemo(() => {
    const m = new Map();
    data?.roles?.forEach(t => m.set(t.id, t));
    return m;
  }, [data]);
};


export const useStationCurrency = () => {
  const { t } = useTranslation();
  const { priceUnit } = useStation();
  return t(`order:enums:currencyShortHand:${OrderEnumsLists.Currency[priceUnit]}`);
};

export const getCurrencyShorthand = (t, priceUnit) => {
  return !priceUnit ? "" : t(`order:enums:currencyShortHand:${OrderEnumsLists.Currency[priceUnit === undefined ? 0 : priceUnit]}`);
};

export const getCurrency = (t, priceUnit) => {
  return t(`order:enums:currency:${OrderEnumsLists.Currency[priceUnit === undefined ? 0 : priceUnit]}`);
};

